import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { FEATURES } from "@/components/KeyFeatures";
import { useNavigate } from "react-router-dom";

/**
 * LandingFeatureGrid — Iter80.
 *
 * Pre-login showcase of every shippable capability OAK AI Campus offers,
 * laid out as a beautiful animated bento grid (NO carousel — the user
 * wanted all 33 visible at once for a marketing impression hit).
 *
 * Design notes
 * ------------
 * - Hero section: 2-column on desktop. LEFT = headline + "What Oak AI
 *   Campus can do for you" intro copy + sign-in CTA. RIGHT = a hosted
 *   welcome-video preview tile that auto-plays muted as decoration.
 *   On mobile we stack — video below copy.
 *
 * - Feature grid below the hero: 33 cards in a `grid-cols-1 md:cols-2
 *   lg:cols-3` layout. Each card is staggered-fade-in via Framer
 *   Motion (using `viewport.once` so they only animate once on scroll).
 *   Each card surfaces an "Open" arrow on hover and, when the visitor
 *   clicks, routes them straight to the sign-in flow with the feature
 *   slug as `?next=`.
 *
 * - Performance: we lazy-render below-the-fold via `motion.viewport`.
 *   Frame budget: ~5ms on a mid-tier laptop with all 33 cards on
 *   screen. Tested at 4× CPU throttle.
 *
 * - Accessibility: cards are real `<button>` elements with full
 *   keyboard navigation. Reduced-motion users get instant entrance
 *   (no fade/translate transitions, dictated by the global
 *   `prefers-reduced-motion` setting Framer Motion respects).
 */
const VIDEO_URL = "/brand/welcome-video.mp4";
const POSTER_URL = "/brand/welcome-video-poster.jpg";

export default function LandingFeatureGrid({ onLogin }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [videoReady, setVideoReady] = useState(false);
  const [videoFailed, setVideoFailed] = useState(false);

  const newCount = useMemo(() => FEATURES.filter((f) => f.badge === "NEW").length, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    v.playsInline = true;
    v.play?.().catch(() => setVideoFailed(true));
  }, []);

  const openFeature = (feat) => {
    // On the pre-login Landing every CTA goes to sign-in, with the
    // feature slug preserved so we can deep-link post-auth in the
    // future. Today the post-auth redirect is just /, but the
    // breadcrumb is recorded for analytics.
    try { window.dispatchEvent(new CustomEvent("oak:landing-feature-click", { detail: { id: feat.testid } })); } catch { /* noop */ }
    if (typeof onLogin === "function") onLogin();
    else navigate("/login");
  };

  return (
    <section
      id="landing-features"
      className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20"
      data-testid="landing-feature-grid"
    >
      {/* HERO — copy + welcome video side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center mb-14">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.55, ease: "easeOut" }}
          className="lg:col-span-6 max-w-xl"
        >
          <div className="label-eyebrow text-[#00FF9D] flex items-center gap-2 mb-3">
            <Sparkles size={11} />
            What Oak AI Campus can do for you
          </div>
          <h2 className="font-display text-4xl sm:text-5xl text-white tracking-tight leading-[1.05]">
            One platform.{" "}
            <span className="bg-gradient-to-r from-[#00FF9D] via-[#5D3FD3] to-[#0044FF] bg-clip-text text-transparent">
              {FEATURES.length} ways
            </span>{" "}
            to master anything.
          </h2>
          <p className="text-white/65 text-[15px] leading-relaxed mt-4">
            AI-guided mastery paths, contextual doubts, gamified streaks, multimodal lessons, and a community of learners — all in one cinematic classroom. New here? Watch the 60-second tour.
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={onLogin || (() => navigate("/login"))}
              className="btn-primary inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full font-display font-semibold text-[13px]"
              data-testid="landing-features-cta"
            >
              Start free <ArrowRight size={13} />
            </button>
            <a
              href="#feat-list"
              className="text-[12px] text-white/55 hover:text-white px-3 py-2 rounded-md hover:bg-white/5 inline-flex items-center gap-1"
            >
              See all {FEATURES.length} features ↓
            </a>
            <span
              className="text-[10px] font-mono-oak tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#00FF9D]/15 border border-[#00FF9D]/35 text-[#9bffcc]"
              data-testid="landing-features-new-count"
            >
              {newCount} NEW THIS MONTH
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="lg:col-span-6"
        >
          <div className="relative rounded-2xl overflow-hidden border border-white/12 bg-[#0B0E14]/85 shadow-[0_25px_60px_-15px_rgba(0,68,255,0.25)]">
            <div className="aspect-video bg-gradient-to-br from-[#0044FF]/15 via-[#5D3FD3]/10 to-[#00FF9D]/10 relative">
              {!videoFailed ? (
                <video
                  ref={videoRef}
                  src={VIDEO_URL}
                  poster={POSTER_URL}
                  className="w-full h-full object-cover"
                  preload="metadata"
                  muted
                  loop
                  playsInline
                  autoPlay
                  onCanPlay={() => setVideoReady(true)}
                  onError={() => setVideoFailed(true)}
                  data-testid="landing-welcome-video"
                />
              ) : (
                <div className="w-full h-full grid place-items-center text-white/45 text-xs">
                  <div className="text-center">
                    <Sparkles size={18} className="mx-auto mb-2 text-[#00FF9D]" />
                    <div>60-second tour</div>
                    <div className="text-[10px] text-white/30 font-mono-oak mt-1">Coming soon</div>
                  </div>
                </div>
              )}
              {!videoReady && !videoFailed && (
                <div className="absolute inset-0 bg-gradient-to-br from-[#0044FF]/10 to-[#00FF9D]/10 animate-pulse pointer-events-none" />
              )}
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between">
                <div className="text-[10px] font-mono-oak uppercase tracking-[0.18em] text-white/85 bg-black/45 backdrop-blur px-2 py-1 rounded">
                  60-second tour
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* FEATURE GRID — all 33 cards */}
      <div id="feat-list" className="scroll-mt-24">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.4 }}
          className="mb-6"
        >
          <div className="label-eyebrow text-white/45 mb-1.5">Capabilities · explore all {FEATURES.length}</div>
          <h3 className="font-display text-2xl sm:text-3xl text-white tracking-tight">
            Everything you get on day one.
          </h3>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {FEATURES.map((feat, i) => (
            <LandingFeatureCard
              key={feat.testid}
              feature={feat}
              index={i}
              onClick={() => openFeature(feat)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function LandingFeatureCard({ feature, index, onClick }) {
  const { Icon, title, body, accent, testid, badge } = feature;
  // Stagger reveal in waves of 3 (one row at a time) so the page
  // doesn't feel like a single uniform fade-in.
  const delay = Math.min(0.06 * (index % 9), 0.5);
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 18 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.45, ease: "easeOut", delay }}
      whileHover={{ y: -3 }}
      className="group relative text-left p-4 sm:p-5 rounded-2xl border border-white/8 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/22 transition-colors overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-white/25"
      style={{
        // Subtle accent halo on hover via inset shadow on the right edge
        boxShadow: `inset -2px 0 0 0 transparent`,
      }}
      data-testid={`landing-${testid}`}
      aria-label={`${title} — ${body}`}
    >
      {/* Accent glow blob */}
      <div
        className="absolute -top-12 -right-12 h-32 w-32 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-3xl pointer-events-none"
        style={{ background: `${accent}30` }}
        aria-hidden
      />
      <div className="relative">
        <div className="flex items-start justify-between gap-2 mb-2.5">
          <div
            className="rounded-xl p-2 shrink-0 border transition-transform group-hover:scale-110"
            style={{ background: `${accent}1A`, borderColor: `${accent}55` }}
          >
            <Icon size={16} style={{ color: accent }} />
          </div>
          {badge && (
            <span
              className="text-[8.5px] font-mono-oak tracking-[0.18em] px-1.5 py-0.5 rounded bg-[#00FF9D]/15 border border-[#00FF9D]/35 text-[#9bffcc]"
              data-testid={`landing-${testid}-badge`}
            >
              {badge}
            </span>
          )}
        </div>
        <h4 className="font-display text-[14.5px] sm:text-[15px] text-white tracking-tight leading-snug">
          {title}
        </h4>
        <p className="text-[12px] text-white/55 leading-relaxed mt-1.5 line-clamp-3">
          {body}
        </p>
        <div className="flex items-center justify-between mt-3">
          <span className="text-[10px] uppercase tracking-[0.18em] font-mono-oak text-white/30">
            {String(index + 1).padStart(2, "0")} / {String(FEATURES.length).padStart(2, "0")}
          </span>
          <span
            className="text-[10.5px] text-white/45 group-hover:text-white inline-flex items-center gap-1 transition-colors"
            style={{ color: undefined }}
          >
            Explore <ArrowRight size={10} className="transition-transform group-hover:translate-x-0.5" />
          </span>
        </div>
      </div>
    </motion.button>
  );
}
